import { forkJoin, Subscription } from 'rxjs';
import {Component, OnDestroy, OnInit} from "@angular/core";
import {
  CalculationPlan,
  CalculationPlanService,
  QuestionService,
} from "../../../../swaggerServices";
import {DropdownListOption} from "../../../shared/components/search-dropdown/search-dropdown.component";
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {TaxYearsService} from "../../../shared/util/tax-years.service";
import {ConfigStateService} from "../../config/config-state.service";
import {Tenant} from "../../../shared/types/tenant.enum";
import { TaxReturnTypeSubTypeService } from "../../../shared/util/tax-return-type-subtype.service";
import { debounceTime } from "rxjs/operators";
import { TenantConfigService } from '../../../shared/services/tenant-config.service';

@Component({
    selector: "app-calculations",
    templateUrl: "./calculations.component.html",
    styleUrls: ["./calculations.component.scss"],
    standalone: false
})
export class CalculationsComponent implements OnInit, OnDestroy {
  questionsById: { [id: string]: { question: string, trail: string, year: number, isGlobal: boolean } };
  questionsByYearAndType: {} = {};
  calculationPlansOriginal: CalculationPlan[];
  calculationPlans: CalculationPlan[];
  loading: boolean;
  taxYearOptions: DropdownListOption[];
  searchForm: UntypedFormGroup = new UntypedFormGroup({});
  taxYear: UntypedFormControl;
  subscriptions: Subscription[] = [];
  showQuestionId: boolean;
  taxReturnTypeOptions: DropdownListOption[];
  taxReturnType: UntypedFormControl;
  yearLabel: string;
  uiConfig: TenantConfigService;

  constructor(private calculationPlanService: CalculationPlanService,
              private questionService: QuestionService,
              private activatedRoute: ActivatedRoute,
              private config: ConfigStateService,
              private taxYearsService: TaxYearsService,
              private taxReturnTypeService: TaxReturnTypeSubTypeService,
  ) {
  }

  ngOnInit() {
    this.loading = true;

    const currYr = this.taxYearsService.getCurrentTaxYear();
    this.taxYear = new UntypedFormControl(currYr);
    this.taxReturnType = new UntypedFormControl('');
    this.searchForm = new UntypedFormGroup({
      taxYear: this.taxYear,
      taxReturnType: this.taxReturnType,
    });

    const getPlans = this.calculationPlanService.getCalculationPlans();
    // TODO: (brad) fix taxReturnTypeId

    this.subscriptions.push(this.taxReturnTypeService.getTaxReturnTypes(false).subscribe((types: any) => {
      this.taxReturnTypeOptions = types;
      const [firstOption] = types;
      this.taxReturnType.setValue(firstOption.value);
    }));

    this.subscriptions.push(this.taxYearsService.getTaxYears().subscribe((years: any) => {
      this.taxYearOptions = years;
      if (!this.taxYearOptions.find(option => option.selected)) {
        const [firstOption] = years;
        this.taxYear.setValue(firstOption.value);
      }
      this.showQuestionId = this.config.tenant !== Tenant.AU;
    }));

    let taxReturnTypeId = ( this.taxReturnType && this.taxReturnType.value) ?  this.taxReturnType.value.value : '1';
    const getQuestions = this.questionService.getQuestionTrail(currYr, taxReturnTypeId);
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.yearLabel=this.uiConfig.config.yearLabel;
      })
    );
    this.subscriptions.push(
      this.taxYear.valueChanges.subscribe(data => {
        const taxYr = data && data.value;
        taxReturnTypeId = ( this.taxReturnType && this.taxReturnType.value) ?  this.taxReturnType.value.value : '1';
        if (taxYr) {
          const type = !taxReturnTypeId ? 0 : Number.parseInt(taxReturnTypeId, 10);
          const year = !taxYr ? 0 : Number.parseInt(taxYr, 10);
          const key = `${year}-${type}`;
          if (this.questionsByYearAndType[key])  {
            this.handleQuestionTrail(this.questionsByYearAndType[key]);
          } else {
            this.loading = true;
            this.questionService.getQuestionTrail(taxYr, taxReturnTypeId).subscribe((questionTrail) => {
              this.loading = false;
              this.questionsByYearAndType[key] = questionTrail;
              this.handleQuestionTrail(questionTrail);
            }, () => {
              this.loading = false;
            });
          }
        }
      })
    );

    forkJoin([getPlans, getQuestions]).subscribe(([calculationPlans, questionTrail]) => {
      this.loading = false;
      this.calculationPlansOriginal = calculationPlans.calculationPlans;
      this.handleQuestionTrail(questionTrail);
    }, () => {
      this.loading = false;
    });

    this.subscriptions.push(this.taxReturnType.valueChanges
      .pipe(debounceTime(400))
      .subscribe(() => {

        taxReturnTypeId = ( this.taxReturnType && this.taxReturnType.value) ?  this.taxReturnType.value.value : '1';

        const taxYr = this.taxYear.value.value ? this.taxYear.value.value : 0;
        const type = !taxReturnTypeId ? 0 : Number.parseInt(taxReturnTypeId, 10);
        const year = !taxYr ? 0 : Number.parseInt(taxYr, 10);
        const key = `${year}-${type}`;
        if (this.questionsByYearAndType[key])  {
          this.handleQuestionTrail(this.questionsByYearAndType[key]);
        } else {
          this.loading = true;
          this.questionService.getQuestionTrail(this.taxYear.value.value, this.taxReturnType.value.value).subscribe((questionTrail) => {
            this.loading = false;
            this.questionsByYearAndType[key] = questionTrail;
            this.handleQuestionTrail(questionTrail);
          }, () => {
            this.loading = false;
          });
        }
      }));

  }


  handleQuestionTrail(questionTrail) {
    this.questionsById = {};
    questionTrail.forEach(q => {
      this.questionsById[q.id] = {
        question: q.question,
        trail: q.trail.join(", "),
        year: q.year,
        isGlobal: q.year === 1900,
      };
    });
    this.calculationPlans = this.calculationPlansOriginal.filter(cp => cp.status !== 99 && this.questionsById[cp.questionId]);
  }

  getCalcPlanSaved(calcPlan: CalculationPlan) {
    // needs to be longer than '[{"instructions":[]}]'
    return calcPlan.plan.length > 21;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

}
